import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Calendar, PackageX, PackagePlus, Repeat, GripVertical, CalendarClock } from "lucide-react";
import { needsOutcomeAt, NEEDS_OUTCOME_CHANGED_EVENT } from "../lib/needsOutcome";
import { getBilling } from "../lib/billing";
import { BillingChip } from "../components/BillingChip";
import TimeGrid from "../components/TimeGrid";
import {
  BOOKING_WINDOW,
  WINDOW_END_HOUR,
  WINDOW_START_HOUR,
  isOutsideWindow,
  sessionOutsideWindow,
} from "../lib/bookingWindow";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import { api, apiError } from "../lib/api";
import { isAwaitingAbn, toastDraftAwaitingAbn } from "../lib/draftToast";
import { COMPLETE_UNDO_MS, undoCompleteAction } from "../lib/undoComplete";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Field } from "./Clients";
import { PageBand, OrangeButton } from "../components/layout/PageShell";
import { useAuth } from "../contexts/AuthContext";
import SessionDetailsDialog from "../components/SessionDetailsDialog";
import { toast } from "sonner";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Default rows = the working window (6 am start … 9 pm start, ending 10 pm).
const HOURS = Array.from(
  { length: WINDOW_END_HOUR - WINDOW_START_HOUR },
  (_, i) => WINDOW_START_HOUR + i
);
const DURATIONS = [30, 45, 60, 90];
const TYPES = [
  { value: "in-person", label: "In-person", color: "#0F6B6B" },
  { value: "online", label: "Online", color: "#1F5C8F" },
  { value: "outdoor", label: "Outdoor", color: "#D9774A" },
];

const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const fmtDay = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
const fmtRange = (a, b) => `${a.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${b.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`;
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// Build YYYY-MM-DD from a Date in the *local* timezone (AEST for this app).
// Never use toISOString() for date-only values — it converts to UTC and silently
// rolls the date back by one day when local offset is positive (e.g. AEST UTC+10).
const toLocalDateIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// DraggableSession — wraps the session card. Only `scheduled` sessions get real
// drag listeners; anything else is rendered inert so completed/cancelled/no-show
// cards stay click-only (tap opens edit).
function DraggableSession({ session, disabled, children, onTap }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: session.session_id,
    data: { session },
    disabled,
  });
  // Guard: if drag actually started (isDragging true even briefly), suppress the
  // subsequent click so users don't accidentally open the edit dialog on drop.
  const draggingRef = useRef(false);
  useEffect(() => {
    if (isDragging) draggingRef.current = true;
  }, [isDragging]);
  const handleClick = (e) => {
    if (draggingRef.current) {
      draggingRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onTap?.(session);
  };
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...(disabled ? {} : listeners)}
      onClick={handleClick}
      style={{ opacity: isDragging ? 0.3 : 1, touchAction: disabled ? "auto" : "none" }}
      data-testid={`draggable-session-${session.session_id}`}
      className="flex-1 min-w-0 h-full cursor-grab active:cursor-grabbing"
    >
      {children}
    </div>
  );
}

// DroppableCell — a single hour/day cell that accepts drops. Highlights on hover.
// Fixed height so booked rows never grow taller than empty rows; cards inside
// the cell truncate to fit and lay out side-by-side when multiple share a slot.
// `flex` is applied DIRECTLY on the cell (not an inner wrapper) so a tap on
// empty space keeps `e.target === e.currentTarget` — that's the guard that
// distinguishes "clicked empty background" from "clicked a session card
// inside the cell" and gates the Book-Session dialog open.
function DroppableCell({ id, data, onEmptyClick, children }) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return (
    <div
      ref={setNodeRef}
      className={`border-l tm-divider h-16 p-0.5 hover:bg-[#F3EDE4] cursor-pointer relative transition-colors overflow-hidden min-w-0 flex gap-1 ${isOver ? "bg-[#0F6B6B]/10 outline outline-2 outline-[#0F6B6B]/60" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onEmptyClick();
      }}
      data-testid={id}
    >
      {children}
    </div>
  );
}

// Convert "Chloe Sinclair" → "Chloe S." for narrow phone widths so the name
// stays legible instead of clipping mid-word. Single-word names pass through.
function shortenName(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

// Visual status → colour dot (mobile) so the status is legible without
// stealing a whole text line. Matches the status colour vocabulary below.
const STATUS_COLOR = {
  scheduled: "#B9AE9E", // grey
  completed: "#1D6B4A", // green
  "no-show": "#D9774A", // terracotta
  cancelled: "#6B635C", // slate-500 (should never render — filtered upstream)
};

// Class-block accent colour by status (colour only — no logic change).
// completed/finished → green, past-but-unmarked (needs outcome) → amber,
// no-show → terracotta, upcoming scheduled → grey.
function sessionAccent(s) {
  if (s.status === "completed") return "#1D6B4A";
  if (s.status === "no-show") return "#D9774A";
  if (needsOutcomeAt(s)) return "#E9A94B";
  return "#B9AE9E";
}

const LEGEND = [
  { label: "Scheduled", color: "#B9AE9E" },
  { label: "Needs outcome", color: "#E9A94B" },
  { label: "Completed", color: "#1D6B4A" },
  { label: "No-show", color: "#D9774A" },
];

export default function Schedule() {
  const { readOnly } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  // Extends the grid rows to cover legacy sessions outside 6 am–10 pm.
  const [showOutside, setShowOutside] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_id: "", date: "", time: "", duration_min: 60, session_type: "in-person", rate: "", notes: "" });
  const [saving, setSaving] = useState(false);
  // Package-empty warning: { intent: "book"|"complete", client, onProceed }
  const [packWarning, setPackWarning] = useState(null);
  // "Don't ask me again for this client" checkbox inside the packWarning
  // dialog. Reset every time the dialog opens; persisted via a POST to
  // /clients/{cid}/warn-pack-empty only when Log anyway is clicked.
  const [packDontAsk, setPackDontAsk] = useState(false);
  // Backdated-class warning: { client, pack } — set only when the class date
  // is before the purchase date of the pack FIFO would take the credit from.
  // No credit is spent until the trainer answers.
  const [backdateWarning, setBackdateWarning] = useState(null);
  // Drag-and-drop rescheduling state
  const [activeDrag, setActiveDrag] = useState(null); // {session}
  // pendingReschedule: gates the drag-drop "Who asked?" prompt (every move)
  // and then the "just this vs all future" scope choice for series sessions.
  // { session, newStartsAt } — set on every drop.
  const [pendingReschedule, setPendingReschedule] = useState(null);
  // For a series drag: scope ("single" | "all"), chosen BEFORE who-asked.
  // Null for one-off sessions (no scope step).
  const [reschedScope, setReschedScope] = useState(null);
  // rescheduleReport: skipped list from series-move response
  // { moved: [], skipped: [{old_starts_at, reason, clash_with}], series: bool }
  const [rescheduleReport, setRescheduleReport] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);

  const sensors = useSensors(
    // Desktop: require a small drag distance so clicks don't trigger drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Mobile: long-press ~180ms to start dragging; tap still opens edit dialog.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.client_id, c])), [clients]);
  const invoiceBySession = useMemo(
    () => Object.fromEntries(invoices.filter((i) => i.session_id).map((i) => [i.session_id, i])),
    [invoices],
  );
  // Pack sale invoices + pack docs, so a pack-covered class can show
  // "PACK · INV-xxxx" (the pack's own invoice) and the credits-left line.
  const packById = useMemo(
    () => Object.fromEntries(packages.map((p) => [p.package_id, p])),
    [packages],
  );
  const invoiceByPackage = useMemo(
    () => Object.fromEntries(invoices.filter((i) => i.package_id).map((i) => [i.package_id, i])),
    [invoices],
  );
  const billingCtx = useMemo(
    () => ({ invoiceBySession, packById, invoiceByPackage }),
    [invoiceBySession, packById, invoiceByPackage],
  );

  const load = useCallback(async () => {
    const [{ data: ss }, { data: cl }, { data: inv }, { data: pk }] = await Promise.all([
      api.get("/sessions", { params: { start: weekStart.toISOString(), end: weekEnd.toISOString() } }),
      api.get("/clients"),
      api.get("/invoices"),
      api.get("/packages/assigned"),
    ]);
    setSessions(ss);
    setClients(cl);
    setInvoices(inv);
    setPackages(pk);
  }, [weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);

  // The Needs-outcome pop-up marks sessions Complete/No-show/Cancel from
  // anywhere in the app. Reload the grid, cards and invoices when it does so
  // the status/invoice chip updates immediately — no tab change or reload.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener(NEEDS_OUTCOME_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(NEEDS_OUTCOME_CHANGED_EVENT, onChanged);
  }, [load]);

  // Consume ?pack_sold=<client_id> pushed from Packages after the trainer
  // sells a new pack via the pack-empty warning. Waits until clients load
  // so we can toast the client's name; then strips the param.
  const packSoldToastedRef = useRef(false);
  useEffect(() => {
    if (packSoldToastedRef.current) return;
    const cid = params.get("pack_sold");
    if (!cid) return;
    if (!clients.length) return;
    packSoldToastedRef.current = true;
    const cli = clients.find((c) => c.client_id === cid);
    const name = cli?.name || "client";
    const remaining = cli ? Number(cli.pack_remaining || 0) : 0;
    toast.success(`Pack sold to ${name} · ${remaining} session${remaining === 1 ? "" : "s"} loaded. Safe to book.`);
    params.delete("pack_sold");
    setParams(params, { replace: true });
  }, [params, setParams, clients]);

  const openCreate = useCallback((dateIso, timeStr) => {
    // Read-only: no new bookings. Guards the New Session button, empty-cell
    // taps, and the ?new=1 deep link in one place.
    if (readOnly) return;
    // Tapping a row outside the working window (only visible after "Show
    // them") must not open a booking we would have to reject.
    if (timeStr && isOutsideWindow(timeStr, 60)) {
      toast.error(BOOKING_WINDOW.message);
      return;
    }
    setEditing(null);
    const d = dateIso || toLocalDateIso(new Date());
    setForm({
      client_id: "",
      date: d,
      time: timeStr || "09:00",
      duration_min: 60,
      session_type: "in-person",
      rate: "",
      notes: "",
    });
    setOpen(true);
  }, [readOnly]);

  const handledNewParam = useRef(false);
  useEffect(() => {
    if (handledNewParam.current) return;
    if (params.get("new")) {
      handledNewParam.current = true;
      openCreate();
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams, openCreate]);

  const clientHasEmptyPack = useCallback((clientId) => {
    const cli = clientMap[clientId];
    return !!(cli && cli.pack_ever && Number(cli.pack_remaining || 0) === 0);
  }, [clientMap]);

  // Same as `clientHasEmptyPack`, but ALSO respects the per-client
  // `warn_pack_empty` opt-out. Only used at the two `setPackWarning` gates —
  // the roster empty-pack badge, the Empty Pack filter and the uncovered-
  // session tag intentionally keep reading `clientHasEmptyPack` (or the
  // underlying pack_remaining) so the dashboard visibility of the state
  // is unchanged when a trainer just silences the interstitial.
  const shouldWarnPackEmpty = useCallback((clientId) => {
    if (!clientHasEmptyPack(clientId)) return false;
    const cli = clientMap[clientId];
    return cli?.warn_pack_empty !== false;
  }, [clientHasEmptyPack, clientMap]);

  const doBook = async (skipPack = false) => {
    setSaving(true);
    try {
      const starts_at = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post("/sessions", {
        client_id: form.client_id,
        starts_at,
        duration_min: Number(form.duration_min),
        session_type: form.session_type,
        rate: form.rate === "" || form.rate == null ? null : Number(form.rate),
        notes: form.notes,
        skip_pack: !!skipPack,
      });
      toast.success(skipPack ? "Session booked — no pack credit used" : "Session booked");
      setOpen(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.clash_with) {
        toast.error(detail.message || "Time slot clashes with another booking", {
          duration: 6000,
          description: `${detail.clash_with.date_mel || ""} ${detail.clash_with.time_mel || ""} — ${detail.clash_with.client_name}`,
        });
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not save session");
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.date || !form.time) { toast.error("Set date & time"); return; }
    if (isOutsideWindow(form.time, form.duration_min)) { toast.error(BOOKING_WINDOW.message); return; }
    if (shouldWarnPackEmpty(form.client_id)) {
      setPackWarning({ intent: "book", client: clientMap[form.client_id], proceed: doBook });
      return;
    }
    // Backdated class on a newer pack: ask before spending the credit. Only
    // fires when the class date is before the pack's purchase date — normal
    // bookings go straight through.
    setSaving(true);
    let pack = null;
    try {
      const starts_at = new Date(`${form.date}T${form.time}:00`).toISOString();
      const { data } = await api.get(`/clients/${form.client_id}/pack-credit-check`, { params: { starts_at } });
      if (data?.backdated) pack = data.pack;
    } catch {
      // Check unavailable — behave exactly as before (book normally).
    } finally {
      setSaving(false);
    }
    if (pack) {
      setBackdateWarning({ client: clientMap[form.client_id], pack });
      return;
    }
    await doBook();
  };

  const doComplete = useCallback(async (s) => {
    try {
      const { data } = await api.put(`/sessions/${s.session_id}/status`, { status: "completed" });
      const inv = data?.auto_invoice;
      const clientName = clientMap[s.client_id]?.name || s.client_name || "client";
      const hasEmail = !!(clientMap[s.client_id]?.email || s.client_email);
      const undo = {
        duration: COMPLETE_UNDO_MS,
        action: undoCompleteAction(s.session_id, load),
      };
      if (inv) {
        const total = `AUD $${Number(inv.total).toFixed(2)}`;
        toast.success(`Invoice ${inv.invoice_number} (${total}) created`, {
          ...undo,
          description: isAwaitingAbn(inv)
            ? "Saved as a draft until your ABN is added — nothing emailed."
            : hasEmail
              ? `Sending to ${clientName} shortly — undo now and no email goes out.`
              : `${clientName} has no email on file, so nothing will be sent.`,
        });
        if (isAwaitingAbn(inv)) toastDraftAwaitingAbn(navigate);
      } else {
        toast.success("Marked completed", undo);
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiError(e, "Could not mark this session complete")); }
  }, [clientMap, load, navigate]);

  const handleComplete = useCallback((s) => {
    if (shouldWarnPackEmpty(s.client_id) && !s.package_id) {
      setPackWarning({ intent: "complete", client: clientMap[s.client_id], proceed: () => doComplete(s) });
      return;
    }
    doComplete(s);
  }, [shouldWarnPackEmpty, clientMap, doComplete]);

  // Post the reschedule request. When applyToSeries is true and there are
  // skipped conflicts, we surface them in the rescheduleReport dialog so the
  // trainer can reconcile clashes by hand.
  const doReschedule = useCallback(async (session, newStartsAt, applyToSeries, rescheduledBy) => {
    if (session.status !== "scheduled") {
      toast.error("Only scheduled sessions can be moved");
      return;
    }
    setRescheduling(true);
    try {
      const { data } = await api.patch(`/sessions/${session.session_id}/reschedule`, {
        starts_at: newStartsAt,
        apply_to_series: !!applyToSeries,
        rescheduled_by: rescheduledBy === "client" ? "client" : "trainer",
      });
      await load();
      const movedCount = (data?.moved || []).length;
      const skipped = data?.skipped || [];
      if (applyToSeries) {
        setRescheduleReport({
          moved: movedCount,
          skipped,
          series: true,
          movedInvoiceCount: (data?.moved_invoice_ids || []).length,
        });
      } else {
        const inv = (data?.moved_invoice_ids || []).length;
        toast.success(
          `Session moved to ${new Date(newStartsAt).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` +
          (inv ? ` · linked invoice date shifted` : ""),
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not move session";
      toast.error(detail);
    } finally {
      setRescheduling(false);
      setPendingReschedule(null);
      setReschedScope(null);
    }
  }, [load]);

  // dnd-kit onDragEnd — computes the new start time from the target cell's
  // {dateIso, hour} data payload, preserving original minutes so half-past
  // slots stay half-past. If the session belongs to a series, prompt first;
  // otherwise fire immediately.
  const onDragStart = useCallback((event) => {
    const s = event?.active?.data?.current?.session;
    if (s) setActiveDrag({ session: s });
  }, []);

  const onDragEnd = useCallback((event) => {
    setActiveDrag(null);
    if (readOnly) return;
    const s = event?.active?.data?.current?.session;
    const target = event?.over?.data?.current;
    if (!s || !target) return;
    // Compose new start from cell date + hour, keeping original minutes/seconds
    const orig = new Date(s.starts_at);
    const [y, m, d] = target.dateIso.split("-").map(Number);
    const next = new Date(y, m - 1, d, target.hour, orig.getMinutes(), 0, 0);
    // No-op if dropped back on its own slot — nothing saved, nothing counted.
    if (next.getTime() === orig.getTime()) return;
    // Clash check FIRST — before asking "Who asked?". If the target slot is
    // already taken by another non-cancelled booking, refuse with the
    // Melbourne-time toast and save nothing (no dialog). The server re-checks
    // on save as a backstop; this pre-check keeps the prompt from ever opening
    // on a slot we already know is booked. (Series "all future" peer clashes
    // are still handled server-side after the scope choice.)
    const dur = Number(s.duration_min || 60);
    const startMin = next.getHours() * 60 + next.getMinutes();
    if (startMin < BOOKING_WINDOW.earliestStartMin || startMin + dur > BOOKING_WINDOW.latestEndMin) {
      toast.error(BOOKING_WINDOW.message);
      return;
    }
    const nextEnd = new Date(next.getTime() + dur * 60000);
    const clash = sessions.find((o) => {
      if (o.session_id === s.session_id || o.status === "cancelled") return false;
      const oStart = new Date(o.starts_at);
      const oEnd = new Date(oStart.getTime() + Number(o.duration_min || 60) * 60000);
      return next < oEnd && oStart < nextEnd;
    });
    if (clash) {
      const whenMel = new Date(clash.starts_at).toLocaleString("en-AU", {
        timeZone: "Australia/Melbourne", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      });
      toast.error(`Time slot conflicts with ${clientMap[clash.client_id]?.name || clash.client_name || "another booking"} at ${whenMel}`);
      return;
    }
    const newIso = next.toISOString();
    // Reset the flow: series sessions pick scope first, then who; one-off
    // sessions go straight to the who-asked step.
    setReschedScope(null);
    setPendingReschedule({ session: s, newStartsAt: newIso });
  }, [sessions, clientMap, readOnly]);

  // Old data can sit outside the working window. The grid must never hide a
  // session: count them, offer to extend the rows, change nothing.
  const outsideSessions = useMemo(
    () => sessions.filter((s) => s.status !== "cancelled" && sessionOutsideWindow(s)),
    [sessions]
  );
  const visibleHours = useMemo(() => {
    if (!showOutside || outsideSessions.length === 0) return HOURS;
    const hours = new Set(HOURS);
    for (const s of outsideSessions) {
      const d = new Date(s.starts_at);
      const startHour = d.getHours();
      const endHour = Math.ceil(
        (startHour * 60 + d.getMinutes() + Number(s.duration_min || 60)) / 60
      );
      for (let h = startHour; h < Math.max(endHour, startHour + 1); h += 1) {
        if (h >= 0 && h <= 23) hours.add(h);
      }
    }
    return [...hours].sort((a, b) => a - b);
  }, [showOutside, outsideSessions]);

  const sessionsByDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    for (const s of sessions) {
      const d = new Date(s.starts_at);
      const day = (d.getDay() + 6) % 7;
      map[day].push(s);
    }
    return map;
  }, [sessions]);

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <PageBand
        testid="schedule-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              Week of {fmtRange(weekStart, addDays(weekEnd, -1))}
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Schedule
            </h1>
          </div>
        }
        right={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur px-1 py-1" data-testid="week-controls">
              <button
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                data-testid="week-prev"
                aria-label="Previous week"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setWeekStart(startOfWeek(new Date()))}
                data-testid="week-today"
                className="px-3 h-9 inline-flex items-center rounded-full text-white text-sm font-semibold hover:bg-white/20 transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                data-testid="week-next"
                aria-label="Next week"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full text-white hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <OrangeButton
              onClick={() => openCreate()}
              disabled={readOnly}
              title={readOnly ? "Your account is read only" : undefined}
              data-testid="add-session-button"
              className="disabled:opacity-40"
              style={{ minHeight: 40 }}
            >
              <Plus className="w-4 h-4" /> New Session
            </OrangeButton>
          </div>
        }
      />

      {/* Reminders placeholder */}
      <div className="tm-card p-4 flex items-center gap-3 text-sm" data-testid="reminders-placeholder">
        <Calendar className="w-4 h-4 text-[#0F6B6B]" />
        <span className="text-[#2A2623]">SMS & email reminders. <span className="text-[#6B635C]">Coming soon.</span></span>
      </div>

      {outsideSessions.length > 0 && !showOutside && (
        <button
          type="button"
          onClick={() => setShowOutside(true)}
          data-testid="outside-window-banner"
          className="w-full text-left rounded-lg border border-[#ECDCA6] bg-[#FBF4DD] px-4 py-3 text-sm text-[#7A5E18] hover:bg-[#F6ECC9] transition-colors"
        >
          {outsideSessions.length} class{outsideSessions.length === 1 ? " this week is" : "es this week are"}
          {" "}outside 6 am to 10 pm.{" "}
          <span className="font-bold underline" data-testid="outside-window-show">Show them.</span>
        </button>
      )}

      {/* Weekly calendar */}
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveDrag(null)}>
      {/*
        Fixed-height scrolling calendar (mobile only) so BOTH sticky axes
        work when the calendar is too tall to fit:
        • `sm:max-h-none` — desktop is untouched; the tm-card grows to fit
          all rows so page-vertical scroll behaves exactly as before.
        • On phones (<sm) we cap tm-card at `calc(100dvh-220px)` and set
          `overflow-auto` so tm-card becomes the nearest scroll ancestor
          for BOTH sticky axes. Users scroll WITHIN the calendar block to
          reach earlier/later hours (Google Calendar / Outlook pattern),
          so the day-header row (sticky top-0) AND the hour-label column
          (sticky left-0 on each hour cell) stay visible at all times.
        • `100dvh` (dynamic viewport height) accounts for the mobile url
          bar shrinking, so the calendar never jumps when the address bar
          hides/shows.
      */}
      <div
        className="tm-card overflow-auto max-h-[calc(100dvh-220px)] sm:max-h-none sm:overflow-x-auto sm:overflow-y-visible"
        data-testid="week-grid"
      >
        <div className="min-w-[820px]">
          {/* Day headers */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b tm-divider sticky top-0 bg-[#FFFFFF] z-20">
            {/* Empty top-left corner — sticks in both axes so it always covers
                the intersection of the pinned hour column and the pinned
                day-header row. */}
            <div className="sticky left-0 bg-[#FFFFFF] z-30" />
            {DAY_LABELS.map((d, i) => {
              const date = addDays(weekStart, i);
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div key={d} className={`p-3 text-center border-l tm-divider ${isToday ? "bg-[#0F6B6B]/10" : ""}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${isToday ? "text-[#0F6B6B]" : "text-[#6B635C]"}`}>{d}</div>
                  <div className={`font-display text-xl font-bold mt-0.5 ${isToday ? "text-[#0F6B6B]" : ""}`}>{fmtDay(date)}</div>
                </div>
              );
            })}
          </div>

          {/* Hour rows */}
          {visibleHours.map((h) => (
            <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b tm-divider">
              {/* Hour label — sticky on the horizontal-scroll axis so the
                  timeline column stays visible when the grid is scrolled
                  sideways on phone widths (min-w-820 > 360/390 viewport). */}
              <div className="p-2 text-xs text-[#6B635C] text-right pr-3 pt-2 font-mono sticky left-0 z-10 bg-[#FFFFFF]">
                {h.toString().padStart(2, "0")}:00
              </div>
              {Array.from({ length: 7 }, (_, dayIdx) => {
                const day = addDays(weekStart, dayIdx);
                // Cancelled sessions are never drawn on the grid — the row
                // stays visually empty as soon as a session is cancelled
                // (from the details dialog, End series, or Skip). The row
                // stays in the DB, marked cancelled, still visible in the
                // client's Sessions tab, exports and invoices.
                const cellSessions = sessionsByDay[dayIdx].filter(
                  (s) => new Date(s.starts_at).getHours() === h && s.status !== "cancelled",
                );
                const dateIso = toLocalDateIso(day);
                const cellId = `cell-${dayIdx}-${h}`;
                return (
                  <DroppableCell
                    key={dayIdx}
                    id={cellId}
                    data={{ dateIso, hour: h, dayIdx }}
                    onEmptyClick={() => openCreate(dateIso, `${h.toString().padStart(2, "0")}:00`)}
                  >
                    {cellSessions.map((s) => {
                      const accent = sessionAccent(s);
                      const startTime = new Date(s.starts_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
                      const billing = getBilling(s, billingCtx);
                      // "Uncovered" — session logged without a pack for a client who
                      // has ever bought a pack (i.e. Log-anyway or booked before assign).
                      const cli = clientMap[s.client_id];
                      const isUncovered =
                        !s.package_id &&
                        !!cli?.pack_ever;
                      // Only scheduled sessions can be moved. Completed / no-show
                      // stay draggable=false; tap still opens the edit dialog.
                      // Read-only locks dragging entirely.
                      const dragDisabled = s.status !== "scheduled" || readOnly;
                      // Mobile: when 2+ live sessions share a slot, each card
                      // collapses to a single line inside the same fixed row
                      // height so they truly sit side-by-side without clipping
                      // or stretching. Desktop still renders both lines.
                      const singleLineMobile = cellSessions.length > 1;
                      const fullName = cli?.name || s.client_name || "(client removed)";
                      const shortName = shortenName(fullName);
                      const statusDot = STATUS_COLOR[s.status] || "#6B635C";
                      // A class whose end time (plus a 10-minute grace) has
                      // passed but is still "scheduled" needs Complete /
                      // No-show marking — nothing is invoiced until then.
                      const needsOutcome = needsOutcomeAt(s);
                      return (
                        <DraggableSession
                          key={s.session_id}
                          session={s}
                          disabled={dragDisabled}
                          onTap={(sx) => setEditing(sx)}
                        >
                          <div
                            data-testid={`session-${s.session_id}`}
                            className="text-left rounded px-1.5 py-1 text-xs hover:brightness-125 transition-all h-full flex-1 min-w-0 overflow-hidden flex flex-col justify-center"
                            style={{ background: `${accent}22`, borderLeft: `5px solid ${accent}` }}
                          >
                            <div className="flex items-center gap-1 min-w-0">
                              {/* Mobile-only status dot — replaces the text
                                  "scheduled/completed/no-show" line. */}
                              <span
                                data-testid={`session-status-dot-${s.session_id}`}
                                aria-label={`Status: ${s.status}`}
                                title={s.status}
                                className="sm:hidden w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ background: statusDot }}
                              />
                              {!dragDisabled && (
                                <GripVertical
                                  data-testid={`session-drag-handle-${s.session_id}`}
                                  className="hidden sm:inline w-3 h-3 text-[#6B635C] shrink-0"
                                  aria-hidden
                                />
                              )}
                              <div className="font-bold truncate text-[#2A2623] flex-1 min-w-0">
                                <span className="sm:hidden" title={fullName}>{shortName}</span>
                                <span className="hidden sm:inline">{fullName}</span>
                              </div>
                              {s.series_id && (
                                <Repeat
                                  data-testid={`session-series-${s.session_id}`}
                                  className="w-3 h-3 text-[#0F6B6B]/80 shrink-0"
                                  aria-label="Part of a recurring series"
                                  strokeWidth={2.5}
                                >
                                  <title>Part of a recurring series — Week {s.series_week_index}</title>
                                </Repeat>
                              )}
                              {isUncovered && (
                                <PackageX
                                  data-testid={`session-uncovered-${s.session_id}`}
                                  className="w-3 h-3 text-[#A8372A] shrink-0"
                                  aria-label="Not covered by a pack"
                                  strokeWidth={2.5}
                                >
                                  <title>Not covered by a pack — logged with "Log anyway" or before a pack was assigned.</title>
                                </PackageX>
                              )}
                            </div>
                            {/* Detail line — hidden on mobile when another
                                session shares this slot so both cards can sit
                                on a single line inside the fixed row height. */}
                            <div
                              className={`text-[10px] text-[#2A2623] truncate ${singleLineMobile ? "hidden sm:block" : ""}`}
                            >
                              <span className="sm:hidden">{startTime}</span>
                              <span className="hidden sm:inline">{startTime} · {s.duration_min}m · {s.status}</span>
                            </div>
                            {needsOutcome && (
                              <div
                                data-testid={`session-needs-outcome-${s.session_id}`}
                                title="This class has passed — mark it Complete or No-show"
                                className={`mt-0.5 inline-flex items-center gap-1 rounded-sm bg-[#FBF4DD] border border-[#ECDCA6] px-1 py-px text-[9px] font-bold text-[#7A5E18] uppercase tracking-wide self-start max-w-full truncate ${singleLineMobile ? "hidden sm:inline-flex" : ""}`}
                              >
                                Outcome?
                              </div>
                            )}
                            {billing && (
                              <div className={`mt-0.5 min-w-0 ${singleLineMobile ? "hidden sm:block" : ""}`}>
                                <BillingChip billing={billing} sessionId={s.session_id} />
                              </div>
                            )}
                          </div>
                        </DraggableSession>
                      );
                    })}
                  </DroppableCell>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <div
            data-testid="drag-overlay"
            className="rounded p-1.5 text-xs shadow-2xl ring-2 ring-[#0F6B6B]/60 bg-[#FFFFFF] pointer-events-none"
            style={{ minWidth: 120 }}
          >
            <div className="font-bold text-[#2A2623] truncate">
              {clientMap[activeDrag.session.client_id]?.name || activeDrag.session.client_name || "session"}
            </div>
            <div className="text-[10px] text-[#2A2623] mt-0.5">Move to a new slot…</div>
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-[#6B635C] flex-wrap" data-testid="schedule-legend">
        {LEGEND.map((t) => (
          <div key={t.label} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded" style={{ background: t.color }} /> {t.label}
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="session-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Book session</DialogTitle>
            <DialogDescription className="sr-only">Schedule a new training session for a client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Client *">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="bg-[#FAF7F2] border-[#E8E1D6]" data-testid="session-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                  {clients.map((c) => (<SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="session-date" className="bg-[#FAF7F2] border-[#E8E1D6] min-w-0 max-w-full box-border" />
            </Field>
            <TimeGrid
              testid="session-time"
              value={form.time}
              dateIso={form.date}
              durationMin={form.duration_min}
              onChange={(t) => setForm({ ...form, time: t })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Duration">
                <Select value={String(form.duration_min)} onValueChange={(v) => setForm({ ...form, duration_min: Number(v) })}>
                  <SelectTrigger className="bg-[#FAF7F2] border-[#E8E1D6]" data-testid="session-duration"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                    {DURATIONS.map((d) => (<SelectItem key={d} value={String(d)}>{d} min</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Type">
                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                  <SelectTrigger className="bg-[#FAF7F2] border-[#E8E1D6]" data-testid="session-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                    {TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Session price override (AUD, incl. GST)">
              <Input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                placeholder="Leave blank to use client's or default rate"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                data-testid="session-rate"
                className="bg-[#FAF7F2] border-[#E8E1D6]"
              />
            </Field>
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="session-notes"
                className="w-full rounded-md bg-[#FAF7F2] border border-[#E8E1D6] px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]" data-testid="session-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="session-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">
              {saving ? "Saving..." : "Book session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Details — shared with the client history drawer so a session
          behaves the same everywhere. Complete runs through Schedule's
          pack-empty warning + auto-invoice toast; no-show / cancel (with Undo)
          / delete are handled inside the shared dialog. */}
      <SessionDetailsDialog
        session={editing}
        onOpenChange={(o) => !o && setEditing(null)}
        clientName={editing ? (clientMap[editing.client_id]?.name || editing.client_name) : ""}
        invoice={editing ? invoiceBySession[editing.session_id] : null}
        billing={editing ? getBilling(editing, billingCtx) : null}
        onComplete={handleComplete}
        onChanged={load}
      />

      <AlertDialog open={!!packWarning} onOpenChange={(o) => { if (!o) { setPackWarning(null); setPackDontAsk(false); } }}>
        <AlertDialogContent
          className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]"
          data-testid="pack-warning-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623] flex items-center gap-2">
              <PackageX className="w-5 h-5 text-[#A8372A]" /> Pack is empty
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[#2A2623] pt-2"
              data-testid="pack-warning-message"
            >
              0 sessions left in pack. This session won't be covered by an existing pack.
              <span className="block text-xs text-[#6B635C] mt-2">
                Client: <span className="text-[#2A2623] font-semibold">{packWarning?.client?.name}</span>
                {" · "}
                {packWarning?.intent === "book" ? "Booking a new session" : "Completing this session"}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Opt-out for pay-per-session clients. Applies ONLY when the trainer
              taps Log anyway — Back / Sell new pack leave the flag untouched. */}
          <label
            className="flex items-start gap-2 text-sm text-[#2A2623] cursor-pointer select-none pt-1"
            data-testid="pack-warning-dont-ask-label"
          >
            <Checkbox
              checked={packDontAsk}
              onCheckedChange={(v) => setPackDontAsk(v === true)}
              data-testid="pack-warning-dont-ask"
              className="mt-0.5 border-[#C9BFAF] data-[state=checked]:bg-[#0F6B6B] data-[state=checked]:text-[#FFFFFF] data-[state=checked]:border-[#0F6B6B]"
            />
            <span>
              Don't ask me again for this client
              <span className="block text-[11px] text-[#6B635C]">
                You can turn the warning back on from the client's profile.
              </span>
            </span>
          </label>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              data-testid="pack-warning-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623] sm:mr-auto"
            >
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const cid = packWarning?.client?.client_id;
                setPackWarning(null);
                setPackDontAsk(false);
                if (cid) navigate(`/packages?assign_client=${cid}`);
              }}
              data-testid="pack-warning-sell"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              <PackagePlus className="w-4 h-4" /> Sell new pack
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                const proceed = packWarning?.proceed;
                const cid = packWarning?.client?.client_id;
                const dontAsk = packDontAsk;
                setPackWarning(null);
                setPackDontAsk(false);
                // Persist the opt-out BEFORE proceeding so a refresh mid-way
                // doesn't leave the client warned again next time. If it
                // fails, still let the booking proceed — the dialog was the
                // interstitial, not a gate on the action.
                if (dontAsk && cid) {
                  try {
                    await api.post(`/clients/${cid}/warn-pack-empty`, { enabled: false });
                    // Reflect the flip locally so subsequent bookings within
                    // this same page load also skip the dialog without waiting
                    // for a full clients reload.
                    setClients((prev) => prev.map((c) => c.client_id === cid ? { ...c, warn_pack_empty: false } : c));
                  } catch {
                    toast.error("Couldn't save 'don't ask again' — warning stays on.");
                  }
                }
                if (proceed) await proceed();
              }}
              data-testid="pack-warning-proceed"
              className="bg-[#EEE9E0] text-[#2A2623] hover:bg-[#E8E1D6] border border-[#E0D8CA]"
            >
              Log anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Backdated class on a newer pack. Nothing has been booked yet — the
          credit is only spent once the trainer picks "Use pack credit".
          "Don't use the pack" books it uncovered, so Complete invoices it
          exactly like a "Log anyway" booking. */}
      <AlertDialog open={!!backdateWarning} onOpenChange={(o) => { if (!o) setBackdateWarning(null); }}>
        <AlertDialogContent
          className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]"
          data-testid="backdated-pack-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623] flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#7A5E18]" /> Class is before this pack
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[#2A2623] pt-2"
              data-testid="backdated-pack-message"
            >
              This class is dated before this pack was bought on {backdateWarning?.pack?.purchased_label}.
              Using the pack will use 1 credit and no invoice will be created.
              <span className="block text-xs text-[#6B635C] mt-2">
                Client: <span className="text-[#2A2623] font-semibold">{backdateWarning?.client?.name}</span>
                {" · "}
                {backdateWarning?.pack?.name} · {backdateWarning?.pack?.sessions_remaining} credit
                {backdateWarning?.pack?.sessions_remaining === 1 ? "" : "s"} left
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              data-testid="backdated-pack-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623] sm:mr-auto"
            >
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { setBackdateWarning(null); await doBook(true); }}
              data-testid="backdated-pack-skip"
              className="bg-[#EEE9E0] text-[#2A2623] hover:bg-[#E8E1D6] border border-[#E0D8CA]"
            >
              Don't use the pack
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => { setBackdateWarning(null); await doBook(false); }}
              data-testid="backdated-pack-use"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              Use pack credit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drag-drop reschedule prompt. Series sessions pick scope (just this /
          all future) FIRST, then "Who asked?"; one-off sessions go straight to
          "Who asked?". The clash check already ran in onDragEnd, so this only
          opens for a slot we know is free. Dismissing saves nothing — the card
          was never moved in local state, so it stays in its original slot. */}
      <AlertDialog
        open={!!pendingReschedule}
        onOpenChange={(o) => { if (!o && !rescheduling) { setPendingReschedule(null); setReschedScope(null); } }}
      >
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="reschedule-series-dialog">
          {(() => {
            const isSeries = !!pendingReschedule?.session?.series_id;
            // Series: show scope step until a scope is chosen, then who-asked.
            // One-off: who-asked immediately.
            const scopeStep = isSeries && reschedScope === null;
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623] flex items-center gap-2">
                    {isSeries && <Repeat className="w-5 h-5 text-[#0F6B6B]" />}
                    {scopeStep ? "Recurring session" : "Who asked?"}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[#2A2623] pt-2">
                    {scopeStep
                      ? "This session is part of a recurring series. Move just this one, or shift every future scheduled session by the same amount?"
                      : "Record who asked for this change. It's kept for your records only — reschedules never change the reliability score."}
                    {pendingReschedule && (
                      <span className="block mt-3 text-xs text-[#6B635C]">
                        New start:{" "}
                        <span className="text-[#2A2623] font-mono">{fmtWhen(pendingReschedule.newStartsAt)}</span>
                      </span>
                    )}
                    <span className="block mt-2 text-[11px] text-[#6B635C]">
                      A linked draft invoice follows the session. Sent or paid invoices are never touched.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {scopeStep ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      disabled={rescheduling}
                      data-testid="reschedule-just-this"
                      onClick={() => setReschedScope("single")}
                      className="bg-[#EEE9E0] text-[#2A2623] hover:bg-[#E8E1D6] border border-[#E0D8CA]"
                    >
                      Move just this
                    </Button>
                    <Button
                      disabled={rescheduling}
                      data-testid="reschedule-all-future"
                      onClick={() => setReschedScope("all")}
                      className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
                    >
                      Move all future
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      disabled={rescheduling}
                      data-testid="reschedule-who-client"
                      onClick={() => doReschedule(pendingReschedule.session, pendingReschedule.newStartsAt, reschedScope === "all", "client")}
                      className="bg-[#FBF4DD] text-[#7A5E18] hover:bg-[#F6ECC9] border border-[#ECDCA6]"
                    >
                      Client asked
                    </Button>
                    <Button
                      disabled={rescheduling}
                      data-testid="reschedule-who-trainer"
                      onClick={() => doReschedule(pendingReschedule.session, pendingReschedule.newStartsAt, reschedScope === "all", "trainer")}
                      className="bg-[#E1EDF7] text-[#1F5C8F] hover:bg-[#D3E4F4] border border-[#B9D4EC]"
                    >
                      I did
                    </Button>
                  </div>
                )}
              </>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={rescheduling}
              data-testid="reschedule-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule report — surfaces the count moved plus each session that
          was skipped because of a clash or a past-time drop, so the trainer
          can reconcile by hand. Only appears after a series move. */}
      <AlertDialog
        open={!!rescheduleReport}
        onOpenChange={(o) => !o && setRescheduleReport(null)}
      >
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] max-w-lg" data-testid="reschedule-report-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
              Series moved
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#2A2623] pt-2" asChild>
              <div>
                <div data-testid="reschedule-report-summary">
                  Moved{" "}
                  <span className="font-bold text-[#0F6B6B]">{rescheduleReport?.moved || 0}</span>{" "}
                  session{(rescheduleReport?.moved || 0) === 1 ? "" : "s"}
                  {rescheduleReport?.movedInvoiceCount ? (
                    <>
                      {" · "}
                      <span className="font-bold text-[#0F6B6B]">{rescheduleReport.movedInvoiceCount}</span> invoice date{rescheduleReport.movedInvoiceCount === 1 ? "" : "s"} shifted
                    </>
                  ) : null}
                  {rescheduleReport?.skipped?.length ? (
                    <>
                      {" · skipped "}
                      <span className="font-bold text-[#7A5E18]">{rescheduleReport.skipped.length}</span>
                    </>
                  ) : null}
                </div>
                {rescheduleReport?.skipped?.length ? (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-1">Skipped — needs manual fix</div>
                    <ul className="space-y-2 max-h-64 overflow-auto pr-2" data-testid="reschedule-report-skipped">
                      {rescheduleReport.skipped.map((sk) => (
                        <li
                          key={sk.session_id}
                          data-testid={`reschedule-skipped-${sk.session_id}`}
                          className="rounded border border-[#ECDCA6] bg-[#FBF4DD] p-2 text-xs"
                        >
                          <div className="text-[#2A2623]">
                            <span className="text-[#6B635C]">Original:</span>{" "}
                            <span className="font-mono">{fmtWhen(sk.old_starts_at)}</span>
                          </div>
                          <div className="text-[#6B635C] text-[11px] mt-0.5">
                            <span className="text-[#6B635C]">Tried to move to:</span>{" "}
                            <span className="font-mono">{fmtWhen(sk.attempted_starts_at)}</span>
                          </div>
                          {sk.reason === "conflict" && sk.clash_with ? (
                            <div className="text-[#7A5E18] mt-1">
                              Clashed with{" "}
                              <span className="font-bold">{sk.clash_with.client_name || "another booking"}</span>{" "}
                              at <span className="font-mono">{fmtWhen(sk.clash_with.starts_at)}</span>
                              {" "}({sk.clash_with.duration_min}m)
                            </div>
                          ) : (
                            <div className="text-[#7A5E18] mt-1">Would fall in the past — skipped.</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-[#6B635C]">All future sessions moved cleanly. No conflicts.</div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setRescheduleReport(null)}
              data-testid="reschedule-report-close"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
